import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { computeIncome, incomeToCsv } from "../utils/incomeCalc";

// Panel de ingresos calificables sobre los movimientos que acaba de extraer el widget
// (30-9-2026). Todo se calcula en el navegador; el factor de gastos y la exclusión de
// transferencias son ajustables porque cada prestamista fija los suyos.

const money = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function IncomePanel({ transactions, t, truncated }) {
  const [factor, setFactor] = useState(50);
  const [exclude, setExclude] = useState(true);
  const r = useMemo(() => computeIncome(transactions, { expenseFactor: factor / 100, excludeTransfers: exclude }), [transactions, factor, exclude]);
  if (!r.monthCount) return null;

  const download = () => {
    const blob = new Blob(["﻿" + incomeToCsv(r)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bank-statement-income.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div data-testid="income-panel" className="mb-4 rounded-2xl border border-primary-100 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">{t("incomeCalc.badge")}</p>
          <p data-testid="income-qualifying" className="mt-1 text-3xl font-bold text-gray-900">
            {money(r.qualifyingMonthly)}
            <span className="ml-1 text-sm font-medium text-gray-500">{t("incomeCalc.per_month")}</span>
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {t("incomeCalc.formula", { avg: money(r.avgMonthlyEligible), factor, months: r.monthCount })}
          </p>
        </div>
        <button type="button" onClick={download} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
          <Download size={14} />
          {t("incomeCalc.download")}
        </button>
      </div>

      {(truncated || r.monthCount < 12) && (
        <p className="mt-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-900">{t("incomeCalc.partial", { months: r.monthCount })}</p>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs font-semibold text-gray-600">
          {t("incomeCalc.expense_factor")}: {factor}%
          <input type="range" min={0} max={90} step={5} value={factor} onChange={(e) => setFactor(Number(e.target.value))} className="mt-1 block h-6 w-full cursor-pointer" style={{ appearance: "auto", accentColor: "#1aa00d" }} />
          <span className="font-normal text-gray-400">{t("incomeCalc.expense_hint")}</span>
        </label>
        <label className="flex items-start gap-2 text-xs text-gray-600">
          <input type="checkbox" className="mt-0.5" checked={exclude} onChange={(e) => setExclude(e.target.checked)} />
          <span>
            <span className="font-semibold">{t("incomeCalc.exclude")}</span>
            <br />
            <span className="text-gray-400">{t("incomeCalc.excluded_total", { total: money(r.excludedTotal), count: r.excluded.length })}</span>
          </span>
        </label>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-gray-500">
            <tr>
              <th className="py-1 pr-3 font-medium">{t("incomeCalc.month")}</th>
              <th className="py-1 pr-3 text-right font-medium">{t("incomeCalc.deposits")}</th>
              <th className="py-1 pr-3 text-right font-medium">{t("incomeCalc.eligible")}</th>
              <th className="py-1 text-right font-medium">{t("incomeCalc.withdrawals")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {r.months.map((m) => (
              <tr key={m.month}>
                <td className="py-1 pr-3">{m.month}</td>
                <td className="py-1 pr-3 text-right">{money(m.deposits)}</td>
                <td className="py-1 pr-3 text-right font-medium">{money(m.eligible)}</td>
                <td className="py-1 text-right">{money(m.withdrawals)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600">
        <span data-testid="income-large">
          {t("incomeCalc.large", { count: r.largeDeposits.length, threshold: money(r.largeThreshold) })}
        </span>
        <span>{t("incomeCalc.nsf", { count: r.nsfCount })}</span>
      </div>
      {r.largeDeposits.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-amber-800">
          {r.largeDeposits.slice(0, 5).map((d, i) => (
            <li key={i}>
              {d.date} · {d.description.slice(0, 60)} · <b>{money(d.amount)}</b>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-[11px] leading-snug text-gray-400">{t("incomeCalc.disclaimer")}</p>
    </div>
  );
}
